import { useState, useEffect } from"react";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { 
 Users, 
 Receipt, 
 UserPlus,
 PlusCircle,
 TrendingUp,
 Building2,
 Briefcase
} from"lucide-react";
import { useNavigate } from"react-router-dom";

const HRDashboard = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const navigate = useNavigate();
 const [loading, setLoading] = useState(true);
 const [stats, setStats] = useState(null);

 useEffect(() => {
 const controller = new AbortController();
 loadDashboard(controller.signal);
 return () => controller.abort();
 }, []);

 const loadDashboard = async (signal) => {
 try {
 const [employees, expenses] = await Promise.all([
   axios.get('/employees/summary/overview', { signal }),
   axios.get('/other-expenses/summary/overview', { signal })
 ]);
 setStats({ employees: { total: employees.data.total_employees, active: employees.data.active_employees, on_leave: employees.data.on_leave },
   expenses: { total: expenses.data.total_expenses, total_amount: expenses.data.total_amount, this_month: expenses.data.this_month } });
 } catch (error) {
 if (!axios.isCancel(error)) console.error("Failed to load dashboard:", error);
 } finally {
 if (!signal?.aborted) setLoading(false);
 }
 };

 const formatCurrency = (amount, currency ="EUR") => {
 return new Intl.NumberFormat('en-EU', {
 style: 'currency',
 currency: currency,
 minimumFractionDigits: 0,
 maximumFractionDigits: 0
 }).format(amount || 0);
 };

 if (loading) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-64">
 <div className="text-sm text-slate-500">{t('common.loading')}</div>
 </div>
 </Layout>
 );
 }

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6">
 {/* Header */}
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">
 HR Dashboard
 </h1>
 <p className="text-sm text-slate-500 mt-1">
 Welcome back, {user?.name}
 </p>
 </div>

 {/* Quick Actions */}
 <div className="grid grid-cols-2 gap-4">
 <Card 
 className="cursor-pointer"
 onClick={() => navigate("/employees")}
 data-testid="card-employees"
 >
 <CardContent className="p-4">
 <div className="flex items-center gap-4">
 <Users className="h-7 w-7 text-blue-600 flex-shrink-0"/>
 <div className="flex-1">
 <p className="text-sm font-medium text-slate-900">Employee Management</p>
 <p className="text-[11px] text-slate-500">Manage employee records and details</p>
 <div className="mt-2">
 <span className="text-2xl font-semibold text-slate-900 tabular-nums">{stats?.employees?.total || 0}</span>
 <span className="text-[11px] text-slate-500 ml-2">total employees</span>
 </div>
 </div>
 </div>
 </CardContent>
 </Card>

 <Card 
 className="cursor-pointer"
 onClick={() => navigate("/other-expenses")}
 data-testid="card-other-expenses"
 >
 <CardContent className="p-4">
 <div className="flex items-center gap-4">
 <Receipt className="h-7 w-7 text-emerald-600 flex-shrink-0"/>
 <div className="flex-1">
 <p className="text-sm font-medium text-slate-900">Other Expenses</p>
 <p className="text-[11px] text-slate-500">Track non-operational expenses</p>
 <div className="mt-2">
 <span className="text-2xl font-semibold text-slate-900 tabular-nums">{formatCurrency(stats?.expenses?.total_amount || 0)}</span>
 <span className="text-[11px] text-slate-500 ml-2">total</span>
 </div>
 </div>
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Summary Cards */}
 <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
 <Card className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-[11px] font-medium text-slate-500">Active Employees</p>
 <p className="text-2xl font-semibold text-slate-900 tabular-nums">{stats?.employees?.active || 0}</p>
 </div>
 <Users className="h-7 w-7 text-emerald-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-[11px] font-medium text-slate-500">On Leave</p>
 <p className="text-2xl font-semibold text-slate-900 tabular-nums">{stats?.employees?.on_leave || 0}</p>
 </div>
 <Briefcase className="h-7 w-7 text-amber-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-[11px] font-medium text-slate-500">Total Expenses</p>
 <p className="text-2xl font-semibold text-slate-900 tabular-nums">{stats?.expenses?.total || 0}</p>
 </div>
 <Receipt className="h-7 w-7 text-blue-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="p-4">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-[11px] font-medium text-slate-500">This Month</p>
 <p className="text-2xl font-semibold text-slate-900 tabular-nums">{formatCurrency(stats?.expenses?.this_month || 0)}</p>
 </div>
 <TrendingUp className="h-7 w-7 text-indigo-600"/>
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Quick Add Buttons */}
 <div className="flex gap-3">
 <Button onClick={() => navigate("/employees?action=add")} className="bg-slate-900 hover:bg-slate-800:bg-zinc-200 text-sm">
 <UserPlus className="w-4 h-4 mr-2"/>
 Add Employee
 </Button>
 <Button onClick={() => navigate("/other-expenses?action=add")} variant="outline"className="text-sm">
 <PlusCircle className="w-4 h-4 mr-2"/>
 Add Expense
 </Button>
 </div>
 </div>
 </Layout>
 );
};

export default HRDashboard;
